// Remote controls for POS devices. Each maps to a manager-api /pos/<command>
// route, which forwards it to the POS's socket (handlers in point-of-sale
// src/context.ts). Keep in sync with the mobile app's
// src/devices/posControls.ts + src/components/DeviceControlsSheet.tsx.
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventIcon from "@mui/icons-material/Event";
import React from "react";
import TimeAgo from "react-time-ago";
import Fetch from "../api/Fetch";
import Toaster from "../common/Toaster";
import { BACKEND_URI } from "../constants";
import type { DeviceInfo } from "./Device";

type Config = DeviceInfo["configuration"];

type PosCommand =
    | "refresh-kiosk"
    | "refresh-event-list"
    | "toggle-out-of-service-message"
    | "set-take-all-mode"
    | "set-kiosk-mode"
    | "set-printer-enabled"
    | "set-offline-mode"
    | "set-square-mode"
    | "set-comps-enabled"
    | "set-boxoffice-enabled";

type SquareMode = "READER" | "TERMINAL" | "MOBILE" | "NONE";

type PosCommandResult = {
    message: string;
    delivered: number;
    targeted: number;
    missing: string[];
};

export type ControlsTarget =
    | { kind: "device"; device: DeviceInfo }
    // nodeNumbers omitted = every connected POS.
    | { kind: "group"; label: string; nodeNumbers?: string[]; onlineCount: number };

const SQUARE_MODES: { value: SquareMode; label: string }[] = [
    { value: "READER", label: "Reader" },
    { value: "TERMINAL", label: "Terminal" },
    { value: "MOBILE", label: "Mobile" },
    { value: "NONE", label: "None" },
];

type ToggleControl = {
    key: string;
    label: string;
    description: string;
    command: PosCommand;
    field: "enabled" | "showMessage";
    // undefined = not reported (no config yet, or an older POS build).
    read: (config: Config) => boolean | undefined;
    unavailable?: (config: Config) => string | null;
    confirm?: string;
};

const isBoxOffice = (config: Config) => (config ? config.salesContext === "BOXOFFICE" : undefined);

const readSquareMode = (config: Config): SquareMode | undefined => (config?.squareMode as SquareMode) || undefined;

const SECTIONS: { title: string; squareMode?: boolean; toggles: ToggleControl[] }[] = [
    {
        title: "Status",
        toggles: [
            {
                key: "outOfService",
                label: "Out of Service",
                description: "Covers the screen with the out-of-service message.",
                command: "toggle-out-of-service-message",
                field: "showMessage",
                read: (c) => c?.outOfService,
            },
            {
                key: "takeAllMode",
                label: "Take All Mode",
                description: "Prints take-all tickets.",
                command: "set-take-all-mode",
                field: "enabled",
                read: (c) => c?.takeAllMode,
            },
        ],
    },
    {
        title: "Sales",
        toggles: [
            {
                key: "boxOffice",
                label: "Box Office",
                description: "Sells as BOXOFFICE instead of POS.",
                command: "set-boxoffice-enabled",
                field: "enabled",
                read: isBoxOffice,
            },
            {
                key: "compsEnabled",
                label: "Comps",
                description: "Allows complimentary tickets.",
                command: "set-comps-enabled",
                field: "enabled",
                read: (c) => (c ? c.salesContext === "BOXOFFICE" && c.compsEnabled : undefined),
                unavailable: (c) => (isBoxOffice(c) === false ? "Requires Box Office." : null),
            },
        ],
    },
    {
        title: "Payments",
        squareMode: true,
        toggles: [
            {
                key: "kioskMode",
                label: "Kiosk Mode",
                description: "Unattended self-serve checkout.",
                command: "set-kiosk-mode",
                field: "enabled",
                read: (c) => c?.kioskMode,
                unavailable: (c) => {
                    const mode = readSquareMode(c);
                    return mode && mode !== "READER" && mode !== "MOBILE" ? "Requires Square Reader or Mobile." : null;
                },
            },
        ],
    },
    {
        title: "Device",
        toggles: [
            {
                key: "printerEnabled",
                label: "Printer",
                description: "Prints receipts and tickets.",
                command: "set-printer-enabled",
                field: "enabled",
                read: (c) => c?.printerEnabled,
            },
            {
                key: "offline",
                label: "Offline Mode",
                description: "Sells from the local cache without a connection.",
                command: "set-offline-mode",
                field: "enabled",
                read: (c) => c?.offline,
                confirm: "Switching offline mode clears the POS's current cart.",
            },
        ],
    },
];

const SQUARE_KEY = "squareMode";
// How long to wait for the POS's next check-in to reflect a change before
// assuming it ignored it (e.g. kiosk mode without a capable Square mode).
const CONFIRM_TIMEOUT_MS = 20_000;
const STALE_AFTER_MS = 120_000;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const sendPosCommand = (command: PosCommand, body: Record<string, unknown>, nodeNumbers?: string[]) =>
    Fetch<PosCommandResult>(`${BACKEND_URI}/pos/${command}`, {
        includeAuth: true,
        method: "POST",
        body: nodeNumbers ? { ...body, nodeNumbers } : body,
    });

type Sent = { value: boolean | SquareMode; at: number };

const DeviceControlsDialog = ({ target, onClose }: { target: ControlsTarget | null; onClose: () => void }) => {
    const device = target?.kind === "device" ? target.device : null;
    const identifier = device ? `${device.identifier}` : null;
    const config: Config = device?.configuration ?? null;
    const isGroup = target?.kind === "group";
    const checkedInAt = device ? Date.parse(device.checkedInAt) : NaN;
    const offline = !isGroup && (isNaN(checkedInAt) || Date.now() - checkedInAt > STALE_AFTER_MS);

    const [sent, setSent] = React.useState<Record<string, Sent>>({});
    const [ignored, setIgnored] = React.useState<Record<string, boolean>>({});
    const [busy, setBusy] = React.useState<string | null>(null);
    const [confirm, setConfirm] = React.useState<{ title: string; message: string; run: () => void } | null>(null);

    // Fresh state whenever the dialog is pointed at something else.
    const targetKey = target ? (target.kind === "device" ? identifier : `${target.label}:${target.nodeNumbers?.join(",")}`) : null;
    React.useEffect(() => {
        setSent({});
        setIgnored({});
        setBusy(null);
        setConfirm(null);
    }, [targetKey]);

    const reported = (key: string): boolean | SquareMode | undefined => {
        if (key === SQUARE_KEY) return readSquareMode(config);
        for (const section of SECTIONS) {
            const toggle = section.toggles.find((t) => t.key === key);
            if (toggle) return toggle.read(config);
        }
        return undefined;
    };

    // Sent values the POS hasn't reported back yet.
    const pending: Record<string, Sent> = {};
    Object.keys(sent).forEach((key) => {
        if (reported(key) !== sent[key].value) pending[key] = sent[key];
    });
    const pendingKeys = Object.keys(pending).join(",");

    React.useEffect(() => {
        if (!pendingKeys) return;
        const keys = pendingKeys.split(",");
        const interval = setInterval(() => {
            const now = Date.now();
            const expired = keys.filter((key) => now - sent[key].at > CONFIRM_TIMEOUT_MS);
            if (expired.length === 0) return;
            setSent((prev) => {
                const next = { ...prev };
                expired.forEach((key) => delete next[key]);
                return next;
            });
            setIgnored((prev) => {
                const next = { ...prev };
                expired.forEach((key) => (next[key] = true));
                return next;
            });
        }, 2_000);
        return () => clearInterval(interval);
    }, [pendingKeys, sent]);

    const send = async (busyKey: string, label: string, command: PosCommand, body: Record<string, unknown>, pendingValue?: boolean | SquareMode) => {
        if (!target) return;
        setBusy(busyKey);
        setIgnored((prev) => ({ ...prev, [busyKey]: false }));
        try {
            const nodeNumbers = target.kind === "device" ? [identifier!] : target.nodeNumbers;
            const result = await sendPosCommand(command, body, nodeNumbers);
            if (result.delivered === 0) {
                Toaster.error(
                    target.kind === "device"
                        ? `POS ${identifier} isn't connected, so "${label}" was not received.`
                        : `No connected devices received "${label}".`
                );
            } else if (result.missing.length > 0) {
                Toaster.error(`"${label}" reached ${plural(result.delivered, "device")}. Not connected: POS ${result.missing.join(", ")}.`);
            } else if (target.kind === "device" && pendingValue !== undefined) {
                setSent((prev) => ({ ...prev, [busyKey]: { value: pendingValue, at: Date.now() } }));
            } else {
                Toaster.success(target.kind === "device" ? `${label} sent.` : `"${label}" delivered to ${plural(result.delivered, "device")}.`);
            }
        } catch (err: any) {
            Toaster.error(err?.message || `${err}`);
        } finally {
            setBusy(null);
        }
    };

    // Group commands and anything flagged as disruptive ask first.
    const request = (busyKey: string, label: string, command: PosCommand, body: Record<string, unknown>, pendingValue?: boolean | SquareMode, warning?: string) => {
        const run = () => send(busyKey, label, command, body, pendingValue);
        if (target?.kind === "group") {
            const scope = target.nodeNumbers
                ? `This is sent to ${plural(target.nodeNumbers.length, "selected device")}.`
                : `This is sent to every connected POS (${plural(target.onlineCount, "device")} currently online).`;
            setConfirm({ title: `${label} on ${target.label}?`, message: [scope, warning].filter(Boolean).join(" "), run });
        } else if (warning) {
            setConfirm({ title: `${label} on POS ${identifier}?`, message: warning, run });
        } else {
            run();
        }
    };

    const statusFor = (key: string, notReported: boolean, reason?: string | null): string | null => {
        if (pending[key]) return "Waiting for POS to confirm…";
        if (ignored[key]) return "POS didn't apply this change.";
        if (reason) return reason;
        if (!isGroup && notReported) return "Not reported by this POS.";
        return null;
    };

    const squareSelected = isGroup ? null : ((pending[SQUARE_KEY]?.value as SquareMode) ?? readSquareMode(config) ?? null);

    return (
        <>
            <Dialog open={!!target} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle sx={{ pr: 6 }}>
                    <Box display="flex" alignItems="center" gap="10px">
                        {isGroup ? (target as any).label : `POS ${identifier}`}
                        {!isGroup && <Chip label={offline ? "Offline" : "Online"} color={offline ? "default" : "success"} size="small" />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {target?.kind === "group" ? (
                            target.nodeNumbers ? (
                                `${plural(target.nodeNumbers.length, "selected device")}`
                            ) : (
                                `Every connected POS · ${plural(target.onlineCount, "device")} online`
                            )
                        ) : (
                            <>
                                {device?.location ? `${device.location} · ` : ""}
                                {!isNaN(checkedInAt) && (
                                    <>
                                        checked in <TimeAgo date={checkedInAt} locale="en-US" />
                                    </>
                                )}
                            </>
                        )}
                    </Typography>
                    <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" gap="16px">
                        {offline && <Alert severity="warning">This POS isn't checking in, so commands probably won't reach it.</Alert>}

                        <Box display="flex" gap="10px">
                            {[
                                { command: "refresh-kiosk" as PosCommand, label: "Refresh Kiosk", icon: <RefreshIcon /> },
                                { command: "refresh-event-list" as PosCommand, label: "Refresh Events", icon: <EventIcon /> },
                            ].map((action) => (
                                <Button
                                    key={action.command}
                                    fullWidth
                                    variant="contained"
                                    disabled={busy !== null}
                                    startIcon={busy === action.command ? <CircularProgress size={18} /> : action.icon}
                                    onClick={() => request(action.command, action.label, action.command, {})}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </Box>

                        {SECTIONS.map((section) => (
                            <Box key={section.title}>
                                <Typography variant="overline" color="text.secondary">
                                    {section.title}
                                </Typography>
                                <Paper variant="outlined" sx={{ px: 2 }}>
                                    {section.squareMode && (
                                        <Box sx={{ py: 1.5 }}>
                                            <Box display="flex" alignItems="center" gap="8px">
                                                <Typography variant="subtitle1">Square Mode</Typography>
                                                {(busy === SQUARE_KEY || !!pending[SQUARE_KEY]) && <CircularProgress size={14} />}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                How card payments are taken.
                                            </Typography>
                                            <ToggleButtonGroup
                                                exclusive
                                                fullWidth
                                                size="small"
                                                color="primary"
                                                value={squareSelected}
                                                disabled={busy !== null}
                                                onChange={(e, mode: SquareMode | null) => {
                                                    if (!mode) return;
                                                    const label = SQUARE_MODES.find((m) => m.value === mode)?.label;
                                                    request(SQUARE_KEY, `Square: ${label}`, "set-square-mode", { mode }, mode);
                                                }}
                                                sx={{ mt: 1 }}
                                            >
                                                {SQUARE_MODES.map((mode) => (
                                                    <ToggleButton key={mode.value} value={mode.value}>
                                                        {mode.label}
                                                    </ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                            <StatusText text={statusFor(SQUARE_KEY, readSquareMode(config) === undefined)} />
                                        </Box>
                                    )}
                                    {section.toggles.map((toggle, i) => {
                                        const reason = isGroup ? null : toggle.unavailable?.(config);
                                        const value = (pending[toggle.key]?.value as boolean | undefined) ?? toggle.read(config);
                                        const setTo = (v: boolean) =>
                                            request(toggle.key, `${toggle.label} ${v ? "On" : "Off"}`, toggle.command, { [toggle.field]: v }, v, toggle.confirm);
                                        return (
                                            <React.Fragment key={toggle.key}>
                                                {(i > 0 || section.squareMode) && <Divider />}
                                                <Box display="flex" alignItems="center" gap="12px" sx={{ py: 1.5 }}>
                                                    <Box flex={1}>
                                                        <Typography variant="subtitle1">{toggle.label}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {toggle.description}
                                                        </Typography>
                                                        <StatusText text={statusFor(toggle.key, toggle.read(config) === undefined, reason)} />
                                                    </Box>
                                                    {isGroup ? (
                                                        <ButtonGroup size="small" disabled={busy !== null}>
                                                            <Button onClick={() => setTo(true)}>On</Button>
                                                            <Button onClick={() => setTo(false)}>Off</Button>
                                                        </ButtonGroup>
                                                    ) : (
                                                        <Box display="flex" alignItems="center" gap="8px">
                                                            {(busy === toggle.key || !!pending[toggle.key]) && <CircularProgress size={14} />}
                                                            <Switch
                                                                checked={!!value}
                                                                disabled={busy !== null || !!reason}
                                                                color="secondary"
                                                                onChange={(e) => setTo(e.target.checked)}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </React.Fragment>
                                        );
                                    })}
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>
            <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{confirm?.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirm?.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            confirm?.run();
                            setConfirm(null);
                        }}
                    >
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const StatusText = ({ text }: { text: string | null }) =>
    text ? (
        <Typography variant="caption" color="secondary" component="div" sx={{ mt: 0.5 }}>
            {text}
        </Typography>
    ) : null;

export default DeviceControlsDialog;
